// Hangar / Dock: the roster as cards. Pending-audio craft are visible, labelled and not selectable (spec §2, §5).
import type { AppContext, ScreenHandle } from './app';
import type { CraftSpec, Domain, EngineSpec } from '../types';
import { CRAFT } from '../data/craft';
import { engineById } from '../data/engines';
import { modById } from '../data/mods';
import { resolveBuild } from '../physics/resolve';
import { predictRange } from '../physics/predict';
import { computeBaseline } from '../sim/score';
import { h, clear, button, photo, confirmDialog } from './dom';
import { fmtNm, fmtCredits, fmtDelta } from './format';
import { engineLine, rangeLabel } from './build';

interface RangeJob { craft: CraftSpec; engine: EngineSpec; modIds: string[]; stock: HTMLElement; build: HTMLElement; delta: HTMLElement }

export function mountHangar(root: HTMLElement, ctx: AppContext): ScreenHandle {
  let tab: Domain = 'air';
  let jobs: RangeJob[] = [];
  let rangeFrame = 0;

  const title = h('h1', { id: 'hangar-title' }, 'Hangar');
  const balance = h('span.badge.num', { 'aria-label': 'Credits balance' }, '');
  const hint = h('div.notice', { role: 'note' });
  const tabs = h('div.tabs', { role: 'tablist', 'aria-label': 'Craft type' });
  const grid = h('div.cards', { role: 'tabpanel' });
  const net = h('span.small.muted', { role: 'status' });
  const footBalance = h('span.num');
  root.append(
    h('div.wrap', {},
      h('header.topbar', {},
        h('div.title', {}, title, h('p', {}, 'Pick a craft. Every number is modelled on published data; every sound is a real recording.')),
        h('div.actions', {}, balance),
      ),
      hint, tabs, grid,
      h('footer.footer', {},
        footBalance,
        button('Settings', () => ctx.go('settings'), { cls: 'btn btn-quiet' }),
        button('Credits', () => ctx.go('credits'), { cls: 'btn btn-quiet' }),
        button('Global board', () => ctx.go('leaderboard'), { cls: 'btn btn-quiet' }),
        h('span.spacer'),
        net,
        h('span.small', {}, ctx.build),
      ),
    ),
  );

  const tabButton = (d: Domain, label: string): HTMLButtonElement => {
    const b = h('button', { class: 'tab', type: 'button', role: 'tab', id: `tab-${d}` }, label);
    b.addEventListener('click', () => { tab = d; render(); });
    return b;
  };
  tabs.append(tabButton('air', 'Aircraft'), tabButton('sea', 'Vessels'));

  function updateNet(): void {
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    net.textContent = offline ? 'Offline · everything works without a connection' : '';
  }
  window.addEventListener('online', updateNet);
  window.addEventListener('offline', updateNet);

  function card(craft: CraftSpec): HTMLElement {
    const engine = engineById(craft.engineId);
    const pending = !engine || engine.audio !== 'verified';
    const owned = ctx.save.owned.includes(craft.id);
    const modIds = ctx.save.builds[craft.id] ?? [];
    const best = ctx.save.bests[craft.id];
    const art = h('article.card', { class: pending ? 'card-pending' : undefined, 'aria-label': `${craft.className}, modelled on ${craft.modelledOn}` });
    art.append(photo(`/img/craft/${craft.photo}.webp`, `${craft.className}, modelled on ${craft.modelledOn}`, craft.modelledOn));
    const stock = h('dd', {}, '…'), build = h('dd', {}, '…'), delta = h('span.small.muted');
    const body = h('div.card-body', {},
      h('h3', {}, craft.className),
      h('p.card-sub', {}, `Modelled on ${craft.modelledOn}`),
      h('p.card-engine', {}, engine ? engineLine(craft, engine) : 'Engine data missing'),
      h('dl.ranges', {},
        h('div', {}, h('dt', {}, rangeLabel(craft.domain)), stock),
        h('div', {}, h('dt', {}, modIds.length ? `Your build · ${modIds.length} mod${modIds.length > 1 ? 's' : ''}` : 'Your build · stock'), build, delta),
      ),
    );
    const status = h('div.card-status');
    if (pending) {
      status.append(h('div', {}, h('p.pending-note', {}, 'Real recording not yet sourced'), engine?.audioNote ? h('p.small.muted', {}, engine.audioNote) : null));
    } else if (owned) {
      status.append(
        h('div', {}, h('span.badge.badge-ok', {}, 'Owned'), best ? h('span.badge.badge-best', { style: 'margin-left:.4rem' }, `Best ${fmtNm(best.nm)}`) : null),
        button('Open workshop', () => ctx.go('workshop', { craftId: craft.id }), { cls: 'btn btn-primary', ariaLabel: `Open workshop: ${craft.className}` }),
      );
    } else {
      const short = craft.costCredits - ctx.save.credits;
      const buy = button(`Buy · ${fmtCredits(craft.costCredits)}`, () => void purchase(craft), {
        cls: 'btn', ariaLabel: `Buy ${craft.className} for ${fmtCredits(craft.costCredits)}`, disabled: short > 0,
      });
      status.append(h('div', {}, h('span.badge', {}, fmtCredits(craft.costCredits)), short > 0 ? h('p.small.muted', {}, `${fmtCredits(short)} short`) : null), buy);
    }
    body.append(status);
    art.append(body);
    if (engine) jobs.push({ craft, engine, modIds, stock, build, delta });
    return art;
  }

  async function purchase(craft: CraftSpec): Promise<void> {
    const ok = await confirmDialog(`Buy ${craft.className}?`, `${fmtCredits(craft.costCredits)} of your ${fmtCredits(ctx.save.credits)}. Purchases are permanent.`, 'Buy');
    if (!ok || ctx.save.credits < craft.costCredits) return;
    ctx.save.credits -= craft.costCredits;
    ctx.save.owned = [...ctx.save.owned, craft.id];
    ctx.persist();
    ctx.toast(`${craft.className} added to the ${craft.domain === 'air' ? 'hangar' : 'dock'}.`);
    render();
  }

  /** Range predictions run the tick model; they fill in after the first paint so the cards never block. */
  function fillRanges(): void {
    for (const j of jobs) {
      const base = computeBaseline(j.craft, j.engine);
      j.stock.textContent = fmtNm(base.rangeNm);
      if (!j.modIds.length) { j.build.textContent = fmtNm(base.rangeNm); continue; }
      const mods = j.modIds.map(modById).filter((m): m is NonNullable<typeof m> => !!m);
      const r = predictRange(resolveBuild(j.craft, j.engine, mods), j.craft.defaultCruise);
      j.build.textContent = fmtNm(r.rangeNm);
      const ratio = base.rangeNm > 0 ? r.rangeNm / base.rangeNm : 1;
      j.build.classList.toggle('better', ratio > 1.0005);
      j.build.classList.toggle('worse', ratio < 0.9995);
      j.delta.textContent = fmtDelta(ratio);
    }
    jobs = [];
  }

  function render(): void {
    jobs = [];
    title.textContent = tab === 'air' ? 'Hangar' : 'Dock';
    balance.textContent = fmtCredits(ctx.save.credits);
    footBalance.textContent = `Balance ${fmtCredits(ctx.save.credits)}`;
    for (const b of tabs.querySelectorAll<HTMLButtonElement>('.tab')) {
      const selected = b.id === `tab-${tab}`;
      b.setAttribute('aria-selected', String(selected));
      b.tabIndex = selected ? 0 : -1;
    }
    grid.setAttribute('aria-labelledby', `tab-${tab}`);
    hint.hidden = ctx.save.runs.length > 0;
    hint.textContent = 'No runs yet. Open a craft, tune its engine in the workshop, plan the route, then go as far as one fuel load allows.';
    clear(grid);
    const list = CRAFT.filter((c) => c.domain === tab);
    if (!list.length) grid.append(h('p.state', {}, 'No craft in this class yet.'));
    for (const c of list) grid.append(card(c));
    updateNet();
    cancelAnimationFrame(rangeFrame);
    rangeFrame = requestAnimationFrame(fillRanges);
  }

  return { show: render };
}
